import { useState, useCallback, useMemo } from 'react';
import { RequestorContext } from './components/RequestorContext';
import { UseCaseSelector } from './components/UseCaseSelector';
import { ConfigBuilder } from './components/ConfigBuilder';
import { ReviewSubmit } from './components/ReviewSubmit';
import { ConfirmationModal } from './components/ConfirmationModal';
import { api, ApiError } from './api/client';
import { USE_CASES, MQM_DEFAULT_PATHS } from './constants/useCases';
import type {
  RequestContext,
  UseCaseKey,
  UseCaseConfig,
  SubmitRequestPayload,
  Environment,
} from './types';
import { Header } from './components/Header';
import './styles/global.css';

interface JobMap {
  [key: string]: UseCaseConfig;
}

export default function App() {
  // Step 1
  const [context, setContext] = useState<Partial<RequestContext>>({
    offering: undefined,
    requestorId: '',
    notifyEmail: '',
  });

  // Step 2 — selected use cases (insertion order preserved)
  const [jobOrder, setJobOrder] = useState<UseCaseKey[]>([]);
  // Step 3 — per-use-case config blob
  const [jobs, setJobs] = useState<JobMap>({});

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    requestId: string;
    ritmNumber: string | null;
  } | null>(null);

  // ── Step 1 validity ─────────────────────────────────────────────────────
  const isStep1Valid = useMemo(() => {
    if (!/^L\d{6}$/i.test(context.requestorId ?? '')) return false;
    if (!context.offering) return false;
    if (context.offering === 'custom') {
      if ((context.teamName ?? '').trim().length < 3) return false;
      if ((context.lineOfBusiness ?? '').trim().length < 3) return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(context.notifyEmail ?? '')) return false;
    return true;
  }, [context]);

  const isStep2Valid = jobOrder.length > 0;
  const isStep3Valid = useMemo(() => {
    if (jobOrder.length === 0) return false;
    return jobOrder.every(k => validateJob(k, jobs[k]));
  }, [jobOrder, jobs]);

// ── Step 2 — single-select toggle ──────────────────────────────────────
  // Current automation supports one use case per RITM. Clicking a selected
  // use case deselects it; clicking a different one replaces the selection
  // (clears any config from the previously selected use case).
  const toggleUseCase = useCallback((key: UseCaseKey) => {
    setJobOrder(prev => {
      if (prev.includes(key)) {
        // Clicking the currently selected use case → deselect
        setJobs({});
        return [];
      }
      // Clicking a different use case → replace selection
      setJobs({ [key]: initJob(key) });
      return [key];
    });
  }, []);

  const updateJob = useCallback((key: UseCaseKey, config: UseCaseConfig) => {
    setJobs(j => ({ ...j, [key]: config }));
  }, []);

  // ── When offering changes, prune incompatible use cases ────────────────
  const handleOfferingChange = useCallback(
    (offering: 'platform' | 'custom') => {
      setContext(c => ({ ...c, offering }));
      // Prune incompatible jobs
      setJobOrder(prev => prev.filter(k => USE_CASES[k].offering === offering));
      setJobs(j => {
        const copy = { ...j };
        Object.keys(copy).forEach(k => {
          if (USE_CASES[k as UseCaseKey].offering !== offering) delete copy[k as UseCaseKey];
        });
        return copy;
      });
    },
    [],
  );

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!isStep1Valid || !isStep2Valid || !isStep3Valid) return;
    setIsSubmitting(true);
    setSubmitError(null);

    const payload: SubmitRequestPayload = {
      context: context as RequestContext,
      useCases: jobOrder.map(k => ({ key: k, config: jobs[k] })),
      idempotencyKey: crypto.randomUUID(),
    };

    try {
      const result = await api.submitRequest(payload);

      // If the integration Lambda completed synchronously, we have the RITM
      if (result.ritmNumber) {
        setConfirmation({ requestId: result.requestId, ritmNumber: result.ritmNumber });
      } else {
        // Otherwise poll for it
        const ritm = await pollForRitm(result.requestId);
        setConfirmation({ requestId: result.requestId, ritmNumber: ritm });
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : (err as Error).message || 'Submission failed';
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [context, jobOrder, jobs, isStep1Valid, isStep2Valid, isStep3Valid]);

  const stepDots: ('active' | 'done' | '')[] = [
    isStep1Valid ? 'done' : 'active',
    isStep1Valid ? (isStep2Valid ? 'done' : 'active') : '',
    isStep2Valid ? (isStep3Valid ? 'done' : 'active') : '',
    confirmation ? 'done' : '',
  ];

  return (
    <>
      <Header dots={stepDots} />
      <main className="main">
        <h1 className="pg-title">
          New Monitoring <span>Onboarding</span> Request
        </h1>
        <p className="pg-desc">
          Configure monitoring for multiple hosts and use cases in a single request. Each use case
          gets its own configuration block with per-host parameters.
        </p>

        {/* Step 1 */}
        <section className="step-sec">
          <StepHeader num={1} label="Request Context" />
          <RequestorContext
            value={context}
            onChange={setContext}
            onOfferingChange={handleOfferingChange}
          />
        </section>

        {/* Step 2 */}
        <section className={'step-sec' + (isStep1Valid ? '' : ' locked')}>
          <StepHeader num={2} label="Select Use Cases" />
          {context.offering && (
            <UseCaseSelector
              offering={context.offering}
              selected={new Set(jobOrder)}
              onToggle={toggleUseCase}
            />
          )}
        </section>

        {/* Step 3 */}
        <section className={'step-sec' + (isStep2Valid ? '' : ' locked')}>
          <StepHeader num={3} label="Configure Hosts & Parameters" />
          <ConfigBuilder jobOrder={jobOrder} jobs={jobs} onUpdate={updateJob} onRemove={toggleUseCase} />
        </section>

        {/* Step 4 — review & submit */}
        <section className={'step-sec' + (isStep3Valid ? '' : ' locked')}>
          <StepHeader num={4} label="Review & Submit" />
          <ReviewSubmit
            context={context as RequestContext}
            jobOrder={jobOrder}
            jobs={jobs}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            error={submitError}
            canSubmit={isStep1Valid && isStep2Valid && isStep3Valid}
          />
        </section>
      </main>

      {confirmation && (
        <ConfirmationModal
          ritm={confirmation.ritmNumber}
          requestId={confirmation.requestId}
          context={context as RequestContext}
          jobOrder={jobOrder}
          jobs={jobs}
          onClose={() => setConfirmation(null)}
        />
      )}
    </>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function StepHeader({ num, label }: { num: number; label: string }) {
  return (
    <div className="step-hdr">
      <div className="step-num">{num}</div>
      <span className="step-lbl">{label}</span>
    </div>
  );
}

function initJob(key: UseCaseKey): UseCaseConfig {
  const def = USE_CASES[key];
  const env: Environment = 'prod';
  const uid = () => `u_${Math.random().toString(36).slice(2, 10)}`;
  switch (def.type) {
    case 'sql':
      return { type: 'sql', group: '', cfgKey: '', blocks: [] };
    case 'oracle':
      return { type: 'oracle', hosts: [{ id: uid(), hostname: '', env }] };
    case 'mqm':
      return {
        type: 'mqm',
        hosts: [{
          id: uid(),
          hostname: '',
          env,
          osType: 'Linux',
          // Pull from the central MQM_DEFAULT_PATHS table so this stays
          // in sync with the spec. User can still override in the UI.
          dirPath: MQM_DEFAULT_PATHS.Linux[env] ?? MQM_DEFAULT_PATHS.Linux.prod,
        }],
      };
    case 'esxiping':
      return { type: 'esxiping', hosts: [], action: 'Add' };
    case 'httpcheck':
      return {
        type: 'httpcheck',
        collectionInterval: '60s',
        initialDelay: '1s',
        optMetrics: {},
        targets: [
          {
            id: uid(),
            endpoint: '',
            method: 'GET',
            env,
            appName: '',
            timeout: '10s',
            expectedStatus: '200',
            tlsSkipVerify: false,
          },
        ],
      };
    case 'procmon':
      return {
        type: 'procmon',
        hosts: [
          {
            id: uid(),
            hostname: '',
            env,
            appName: '',
            osType: 'Linux',
            include: [],
            matchType: 'strict',
            scrapeDelay: '0s',
            muteErrors: {},
            metrics: {},
          },
        ],
      };
    case 'template':
      return { type: 'template' };
    case 'generic':
    default: {
      const params: Record<string, string> = {};
      (def.params ?? []).forEach(p => (params[p.id] = p.defaultValue ?? ''));
      return { type: 'generic', hosts: [{ id: uid(), hostname: '', env, appName: '', params }] };
    }
  }
}

function validateJob(_key: UseCaseKey, cfg: UseCaseConfig | undefined): boolean {
  if (!cfg) return false;
  switch (cfg.type) {
    case 'sql':
      return cfg.group !== '' && cfg.cfgKey !== '' && cfg.blocks.length > 0;
    case 'oracle':
      return !!cfg.uploadId && cfg.hosts.every(h => h.hostname.trim().length > 0);
    case 'mqm':
      return cfg.hosts.every(h => h.hostname.trim().length > 0 && h.dirPath.trim().length > 0);
    case 'esxiping':
      return cfg.hosts.length > 0 && (cfg.action === 'Add' || cfg.action === 'Remove');
    case 'generic':
      return cfg.hosts.every(h => h.hostname.trim().length > 0);
    case 'httpcheck':
      return cfg.targets.every(t => t.endpoint.trim().length > 0);
    case 'procmon':
      return cfg.hosts.every(h => h.hostname.trim().length > 0);
    case 'template':
      return !!cfg.uploadId;
  }
}

/** Poll GET /requests/:id every 2s for up to 30s waiting for the RITM */
async function pollForRitm(requestId: string): Promise<string | null> {
  for (let i = 0; i < 15; i++) {
    await new Promise(r => setTimeout(r, 2000));
    const s = await api.getRequestStatus(requestId);
    if (s.ritmNumber) return s.ritmNumber;
    if (s.status === 'failed') throw new ApiError(500, 'INTEGRATION_FAILED', s.errorMessage ?? 'SNOW integration failed');
  }
  return null;
}
